/**
 * Adding a machine (docs/signing.md §10).
 *
 * `/enroll` — the machine's `viko setup` / `viko enroll` opened this page (or the user scanned
 * its QR): attach the machine's pending claim to this account, then approve with a passkey over an
 * EnrollmentStatement naming exactly that daemon key and this account's passkey set.
 * - Local links (vikod opened this browser; same network): "Add this Mac?" — no words to compare.
 * - Remote links, or a local link opened from another network: the SAS words (5 words + emoji,
 *   derived from machine, key, passkey set and code) must match the terminal, and the page shows
 *   where the request came from.
 *
 * `/settings/enroll` — start from the app: show `viko enroll <token>` for the machine to claim.
 */
import { useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from '@tanstack/react-router'
import {
  approvalBatchItem,
  createApprovalBatchChallenge,
  createEnrollmentChallenge,
  createEnrollmentStatement,
  credentialSetHash,
  DOMAIN,
  deriveEnrollmentSas,
  type EnrollmentApproval,
  jwkThumbprint,
  pinnedCredentialFromCose,
  type Sas,
} from '@viko/crypto'
import { Button } from '@viko/transcript-ui'
import { ChevronLeft, Copy, Fingerprint, Laptop, ShieldAlert, Terminal } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { EnrollmentAccessStep } from '../components/MachineAccess.js'
import { errorText, useToast } from '../components/ui.js'
import { ApiError, type EnrollAttach, type EnrollPeek, type EnrollStart } from '../lib/api.js'
import { appLock } from '../lib/app-lock-controller.js'
import { useBackend } from '../lib/backend.js'
import { pinApprovedContentDaemon } from '../lib/content-enrollment.js'
import type { PreparedGenesis } from '../lib/content-session.js'
import { clearPending, parseEnrollLink, readPending, writePending } from '../lib/links.js'
import { saveLocalMachine } from '../lib/lock-signals.js'
import { passkeys } from '../lib/passkeys.js'
import { enablePresenceUnlock } from '../lib/presence-unlock.js'
import { qk } from '../lib/queries.js'
import { isSessionOnly } from '../lib/session-only.js'

interface Review {
  code: string
  peek: EnrollPeek
  sas: Sas
  /** The server's summary disagrees with the keys it sent. */
  warn?: string
  /** From a link: how it was opened. */
  attach?: EnrollAttach
}

const platformNoun = (p: string | null | undefined) =>
  p === 'darwin' || p === 'mac'
    ? 'Mac'
    : p === 'win32'
      ? 'PC'
      : p === 'linux'
        ? 'Linux machine'
        : 'machine'

/** Does this browser run on the OS the machine claimed? (Local links are opened by vikod itself.) */
export function browserMatchesPlatform(
  platform: string | null | undefined,
  ua: string = globalThis.navigator?.userAgent ?? '',
): boolean {
  if (/iPhone|iPad|Android|Mobile/i.test(ua)) return false
  if (platform === 'darwin' || platform === 'mac') return /Macintosh|Mac OS X/.test(ua)
  if (platform === 'win32') return /Windows/.test(ua)
  if (platform === 'linux') return /Linux|X11|CrOS/.test(ua) && !/Android/.test(ua)
  return false
}

/** SAS + consistency check, recomputed from what the hub sent (never trusting its hash alone). */
async function review(
  code: string,
  peek: EnrollPeek,
  attach?: EnrollAttach,
): Promise<Review | null> {
  if (!peek.machineId || !peek.daemonPublicKey) return null
  const daemonKeyId = await jwkThumbprint(peek.daemonPublicKey)
  const creds = peek.credentials.map((x) => pinnedCredentialFromCose(x.credentialId, x.cose, x))
  const setHash = await credentialSetHash(creds)
  const sas = await deriveEnrollmentSas({
    machineId: peek.machineId,
    daemonKeyId,
    credentialSetHash: setHash,
    code,
  })
  const warn =
    daemonKeyId !== peek.daemonKeyId || setHash !== peek.credentialSetHash
      ? 'The server’s summary of this machine does not match its keys. Do not approve unless the terminal shows exactly these words.'
      : undefined
  return { code, peek, sas, ...(warn ? { warn } : {}), ...(attach ? { attach } : {}) }
}

interface Approved {
  name: string
  machineId: string
  approvedAt: number
}

/**
 * Approve: ONE passkey tap. On a fresh account it covers the EnrollmentStatement and the genesis
 * key directory together (an approval batch, docs/e2e.md "Fewer passkey ceremonies"). The Done
 * step then grants the machine the account key with a device-signed update, no further tap, as
 * soon as vikod publishes its content identity, so its encrypted reports flow without waiting.
 */
function useApprove(onDone: (approved: Approved) => void) {
  const b = useBackend()
  const qc = useQueryClient()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const approve = async (r: Review) => {
    const { peek, code } = r
    if (!peek.machineId || !peek.daemonPublicKey || !peek.fingerprint) return
    setBusy(true)
    setError(null)
    let genesis: PreparedGenesis | undefined
    try {
      const statement = await createEnrollmentStatement({
        machineId: peek.machineId,
        machineName: peek.machineName ?? peek.machineId,
        daemonPublicKey: peek.daemonPublicKey,
        credentials: peek.credentials.map((x) =>
          pinnedCredentialFromCose(x.credentialId, x.cose, x),
        ),
        code,
        rpId: peek.rpId,
      })
      // Fresh account: the same tap also authorizes the genesis key directory.
      genesis = b.content ? await b.content.prepareGenesis().catch(() => undefined) : undefined
      const challenge = await createEnrollmentChallenge(statement)
      const batch = genesis
        ? [approvalBatchItem(DOMAIN.enrollment, challenge), genesis.item]
        : undefined
      const assertion = await passkeys.assert(
        batch ? await createApprovalBatchChallenge(batch) : challenge,
        { rpId: peek.rpId },
      )
      const approval: EnrollmentApproval = { statement, assertion, ...(batch ? { batch } : {}) }
      if (b.content) {
        const accountId = await b.content.accountId()
        await pinApprovedContentDaemon(accountId, approval)
      }
      await b.api.enrollConfirm(code, peek.fingerprint, approval)
      // Best effort: if genesis loses a race, the Done step falls back to a passkey grant.
      if (genesis && batch)
        await genesis.publish({ type: 'passkey', assertion, batch }).catch(() => {})
      // ---- lock-follows-os: a local enrollment names this computer's vikod for the app lock ----
      if (
        r.attach?.mode === 'local' &&
        r.attach.sameNetwork &&
        browserMatchesPlatform(r.attach.platform ?? peek.platform)
      ) {
        void (
          appLock.get()?.setLocalMachine(peek.machineId) ?? saveLocalMachine(peek.machineId)
        ).catch(() => {})
        // passkey-budget WP1: this approval was the passkey tap; follow this computer's login.
        if (b.content) {
          const accountId = await b.content.accountId()
          void enablePresenceUnlock({
            accountId,
            machineId: peek.machineId,
            approvedByEnrollment: true,
          }).catch(() => {})
        }
      }
      // ---- end lock-follows-os ----
      await qc.invalidateQueries({ queryKey: qk.machines })
      onDone({
        name: peek.machineName ?? 'Machine',
        machineId: peek.machineId,
        approvedAt: Date.now(),
      })
    } catch (e) {
      setError(enrollError(e))
    } finally {
      genesis?.discard()
      setBusy(false)
    }
  }
  return { approve, busy, error, setError }
}

function enrollError(e: unknown): string {
  if (e instanceof ApiError) {
    switch (e.code) {
      case 'link_expired':
      case 'expired':
        return 'This link expired. Run `viko setup` (or `viko enroll`) again for a new one.'
      case 'link_used':
        return 'This link was already used. Run `viko enroll` again for a new one.'
      case 'invalid_link':
      case 'unknown_code':
        return 'This link isn’t valid. Copy the whole link from the terminal, or run the command again.'
      case 'quota_machines':
        return 'Your account has reached its machine limit. Remove a machine under You › Machines first.'
      case 'rate_limited':
        return 'Too many attempts. Wait a few minutes and try again.'
    }
  }
  return errorText(e)
}

function SasWords({ sas }: { sas: Sas }) {
  return (
    <div className="sas" role="group" aria-label={`Verification words: ${sas.words.join(', ')}`}>
      {sas.words.map((w, i) => (
        <div key={w}>
          <span className="emoji" aria-hidden="true">
            {sas.emoji[i]}
          </span>
          <span className="word">{w}</span>
        </div>
      ))}
    </div>
  )
}

/** The approval card: "Add this Mac?" (local) or the SAS comparison (remote / other network). */
function ApproveCard({
  r,
  busy,
  onApprove,
  onCancel,
}: {
  r: Review
  busy: boolean
  onApprove: () => void
  onCancel: () => void
}) {
  const noun = platformNoun(r.peek.platform ?? r.attach?.platform)
  // Skip the words only when everything says "this is the machine you're on": a local link, the
  // same public IP as the claim, and a browser on the claimed OS (a phone never qualifies).
  const samePlatform = browserMatchesPlatform(r.attach?.platform ?? r.peek.platform)
  const local = r.attach?.mode === 'local' && r.attach.sameNetwork && samePlatform && !r.warn
  const offNetwork = r.attach?.mode === 'local' && !(r.attach.sameNetwork && samePlatform)
  const trusts = r.peek.credentials.map((c) => c.label ?? 'passkey').join(', ')
  return (
    <div className="grid gap-4">
      <div className="card grid gap-1 p-4">
        <span className="flex items-center gap-2 text-[14px] text-text-3">
          <Laptop size={14} aria-hidden="true" /> New {noun}
        </span>
        <span className="text-[17px] font-semibold">{r.peek.machineName}</span>
        <span className="font-mono text-[14px] text-text-3">Key {r.peek.fingerprint}</span>
      </div>
      {local ? (
        <p className="m-0 text-[15px] text-text-2">
          <code className="font-mono">viko</code> on this computer asked to connect it to your
          account. It will run agent sessions you start from Viko, receive your encryption key for
          your encrypted work, and accept commands only from devices your passkeys trust.{' '}
          <strong className="font-semibold text-text-1">
            Only approve if you just ran viko on this computer.
          </strong>
        </p>
      ) : (
        <div className="grid gap-2">
          <h2 className="m-0 text-[15px] font-semibold">Do these match the terminal?</h2>
          <SasWords sas={r.sas} />
          <p className="m-0 text-[14px] text-text-3">
            Approving also gives it your encryption key. The machine will trust: {trusts}.
            {r.attach?.country ? ` Request from ${r.attach.country}.` : ''}
          </p>
        </div>
      )}
      {offNetwork ? (
        <p className="m-0 flex gap-2 rounded-xl bg-status-fail-soft p-3 text-[14px]" role="alert">
          <ShieldAlert size={16} className="flex-none text-status-fail" aria-hidden="true" />
          This link was opened on a different device or network than the computer that created it.
          Only approve a machine you are setting up yourself, right now. Approval trusts this
          machine to receive your encrypted run content.
        </p>
      ) : null}
      {r.warn ? (
        <p className="m-0 flex gap-2 rounded-xl bg-status-fail-soft p-3 text-[14px]" role="alert">
          <ShieldAlert size={16} className="flex-none text-status-fail" aria-hidden="true" />
          {r.warn}
        </p>
      ) : null}
      <div className="flex flex-wrap gap-2">
        <Button
          variant="primary"
          className="!min-h-11"
          icon={<Fingerprint size={16} />}
          disabled={busy}
          onClick={onApprove}
        >
          {busy
            ? 'Waiting for passkey…'
            : local
              ? `Add this ${noun}`
              : 'They match — approve with passkey'}
        </Button>
        <Button variant="ghost" className="!min-h-11" onClick={onCancel}>
          {local ? 'Cancel' : 'They don’t match'}
        </Button>
      </div>
    </div>
  )
}

function Done({ approved, fromLink }: { approved: Approved; fromLink: boolean }) {
  const nav = useNavigate()
  const { name } = approved
  return (
    <div className="card grid gap-3 p-4" role="status">
      <h2 className="m-0 text-[17px] font-semibold">{name} is connected</h2>
      <p className="m-0 text-[15px] text-text-2">
        {fromLink
          ? 'Return to the terminal: review readiness checks, choose Fix, Recheck, or Skip, then set up background runs. Add a project there with `viko project add /path/to/repo`.'
          : 'It now accepts commands only from devices your passkeys trust.'}
      </p>
      <EnrollmentAccessStep
        machineId={approved.machineId}
        name={name}
        approvedAt={approved.approvedAt}
      />
      <Link to="/settings/providers" className="vk-btn vk-btn--secondary">
        Set up provider accounts (optional)
      </Link>
      <Button
        variant="primary"
        className="!min-h-11"
        onClick={() => void nav({ to: fromLink ? '/home' : '/settings/machines' })}
      >
        Done
      </Button>
    </div>
  )
}

function Page({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="app-list flex-1">
      <div className="mx-auto grid max-w-[560px] gap-5 px-4 pb-10">
        <header className="page-head !px-0">
          <Link to="/settings" className="vk-iconbtn vk-iconbtn--lg" aria-label="Back to Settings">
            <ChevronLeft size={20} />
          </Link>
          <h1>{title}</h1>
        </header>
        {children}
      </div>
    </div>
  )
}

// ---- /enroll (link from vikod) --------------------------------------------------------------------

/** A "Just this session" browser can't approve machines: they would outlive it. */
function SessionOnlyEnroll() {
  return (
    <Page title="Add a machine">
      <div className="card grid gap-2 p-4">
        <h2 className="m-0 text-[15px] font-semibold">Trust this device to add machines</h2>
        <p className="m-0 text-[15px] text-text-2">
          This browser is signed in for this session only. A machine you add keeps trusting the
          device that approved it, so approve it from a trusted device, or sign out and sign in
          again with Trust this device.
        </p>
      </div>
    </Page>
  )
}

export function EnrollLinkPage() {
  if (isSessionOnly()) return <SessionOnlyEnroll />
  return <EnrollLink />
}

function EnrollLink() {
  const b = useBackend()
  const nav = useNavigate()
  const [r, setR] = useState<Review | null>(null)
  const [done, setDone] = useState<Approved | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const started = useRef(false)
  const { approve, busy, error } = useApprove((approved) => {
    clearPending('e')
    setDone(approved)
  })

  useEffect(() => {
    if (started.current) return
    started.current = true
    const link = readPending()?.e
    if (!link) {
      setLoadError(
        'No machine link here. Run `viko enroll` on the machine you want to add — it opens this page.',
      )
      return
    }
    ;(async () => {
      try {
        const at = await b.api.enrollAttach(link)
        const peek = await b.api.enrollPeek(at.code)
        const rv = await review(at.code, peek, at)
        if (!rv) throw new Error('The machine is not ready yet. Try again in a moment.')
        setR(rv)
      } catch (e) {
        if (e instanceof ApiError && ['link_expired', 'link_used', 'invalid_link'].includes(e.code))
          clearPending('e')
        setLoadError(enrollError(e))
      }
    })()
  }, [b])

  const title = done
    ? 'Machine connected'
    : r
      ? `Add this ${platformNoun(r.peek.platform ?? r.attach?.platform)}?`
      : 'Add a machine'
  return (
    <Page title={title}>
      {done ? <Done approved={done} fromLink /> : null}
      {!done && r ? (
        <ApproveCard
          r={r}
          busy={busy}
          onApprove={() => void approve(r)}
          onCancel={() => {
            clearPending('e')
            void nav({ to: '/' })
          }}
        />
      ) : null}
      {!done && !r && !loadError ? (
        <p className="m-0 text-[15px] text-text-2" role="status" aria-live="polite">
          Checking the machine…
        </p>
      ) : null}
      {loadError || error ? (
        <p role="alert" className="m-0 text-[15px] text-status-fail">
          {loadError ?? error}
        </p>
      ) : null}
    </Page>
  )
}

// ---- /settings/enroll (start from the app) -------------------------------------------------------------

type Stage =
  | { k: 'choose' }
  | { k: 'waiting'; start: EnrollStart }
  | { k: 'compare'; r: Review }
  | { k: 'done'; approved: Approved }

export function EnrollPage() {
  if (isSessionOnly()) return <SessionOnlyEnroll />
  return <Enroll />
}

function Enroll() {
  const b = useBackend()
  const toast = useToast()
  const nav = useNavigate()
  const [stage, setStage] = useState<Stage>({ k: 'choose' })
  const [pasted, setPasted] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const approval = useApprove((approved) => setStage({ k: 'done', approved }))

  // Poll until the daemon claims the code.
  useEffect(() => {
    if (stage.k !== 'waiting') return
    let alive = true
    const tick = async () => {
      try {
        const peek = await b.api.enrollPeek(stage.start.code)
        if (!alive) return
        if (peek.status === 'expired')
          return setError('The token expired. Get a new one and run the command again.')
        if (peek.status === 'claimed') {
          const r = await review(stage.start.code, peek)
          if (r && alive) return setStage({ k: 'compare', r })
        }
      } catch (e) {
        if (alive) setError(enrollError(e))
      }
      if (alive) setTimeout(tick, 1500)
    }
    void tick()
    return () => {
      alive = false
    }
  }, [stage, b])

  return (
    <Page title="Add a machine">
      {stage.k === 'choose' ? (
        <>
          <div className="card grid gap-3 p-4">
            <h2 className="m-0 text-[15px] font-semibold">On the new machine</h2>
            <p className="m-0 text-[15px] text-text-2">
              Install viko, then run <code className="font-mono">viko enroll</code> in its terminal.
              It opens a browser there — sign in with your passkey and approve.
            </p>
            <p className="m-0 text-[15px] text-text-2">
              No browser on that machine? Get a one-time token here and run the command it shows.
            </p>
            <Button
              className="!min-h-11"
              icon={<Terminal size={15} />}
              disabled={busy}
              onClick={async () => {
                setBusy(true)
                setError(null)
                try {
                  setStage({ k: 'waiting', start: await b.api.enrollStart() })
                } catch (e) {
                  setError(enrollError(e))
                } finally {
                  setBusy(false)
                }
              }}
            >
              Get enrollment command
            </Button>
          </div>
          <form
            className="card grid gap-3 p-4"
            onSubmit={(e) => {
              e.preventDefault()
              const link = parseEnrollLink(pasted)
              if (!link) {
                setError('Paste the whole link the terminal printed (it starts with https://).')
                return
              }
              writePending({ e: link, kind: 'enroll' })
              void nav({ to: '/enroll' })
            }}
          >
            <label className="field">
              <span>Or paste the link the machine printed</span>
              <input
                className="input font-mono text-[15px]"
                value={pasted}
                onChange={(e) => setPasted(e.target.value)}
                placeholder="https://…/enroll#e=…"
                autoComplete="off"
                spellCheck={false}
                inputMode="url"
              />
            </label>
            <Button type="submit" className="!min-h-11" disabled={pasted.trim().length < 20}>
              Continue
            </Button>
          </form>
        </>
      ) : null}

      {stage.k === 'waiting' ? (
        <div className="card grid gap-3 p-4" aria-live="polite">
          <h2 className="m-0 text-[15px] font-semibold">Run this on the new machine</h2>
          <div className="flex items-center gap-2 rounded-lg bg-code-bg p-3">
            <code className="flex-1 font-mono text-[14px] break-all">
              viko enroll {stage.start.token}
            </code>
            <button
              type="button"
              className="vk-iconbtn vk-iconbtn--lg"
              aria-label="Copy command"
              onClick={() => {
                void navigator.clipboard?.writeText(`viko enroll ${stage.start.token}`)
                toast('Copied')
              }}
            >
              <Copy size={15} />
            </button>
          </div>
          <p className="m-0 flex items-center gap-2 text-[15px] text-text-2">
            <span
              className="vk-spin inline-block h-3 w-3 rounded-full border-2 border-text-3 border-t-transparent"
              aria-hidden="true"
            />
            Waiting for the machine to check in with code{' '}
            <strong className="font-mono">{stage.start.code}</strong>…
          </p>
        </div>
      ) : null}

      {stage.k === 'compare' ? (
        <ApproveCard
          r={stage.r}
          busy={approval.busy}
          onApprove={() => void approval.approve(stage.r)}
          onCancel={() => setStage({ k: 'choose' })}
        />
      ) : null}

      {stage.k === 'done' ? <Done approved={stage.approved} fromLink={false} /> : null}

      {error || approval.error ? (
        <p role="alert" className="m-0 text-[15px] text-status-fail">
          {error ?? approval.error}
        </p>
      ) : null}
    </Page>
  )
}
